import { AlignmentType, BorderStyle, Paragraph, Table, TableCell, TableRow, TextRun, WidthType } from "docx";

export function reportAppName(applicationName) {
    return new Paragraph({
        width: { size: 50, type: WidthType.PERCENTAGE },
        alignment: AlignmentType.CENTER,
        spacing: { line: 276, before: 3700, after: 2000 },
        children: [
            new TextRun({
                text: "Audit Report of " + applicationName + " Source Code",
                font: "Arial",
                size: 52,
                color: '009644',
                allCaps: false,
                italics: false,
                bold: true
            }),
        ],
    });
}

export function auditReportTable(certInDate, certInStartDate, certInEndDate) {
    // Unified date formatter — works for ISO, yyyy-mm-dd, or dd/mm/yyyy
    function formatDateLikeTimeline(dateString) {
        if (!dateString) return "";

        let date;

        // Handle string types: ISO (2025-10-07T00:00:00Z) or dd/mm/yyyy
        if (typeof dateString === "string") {
            if (dateString.includes("T")) {
                // ISO string
                date = new Date(dateString);
            } else if (dateString.includes("/")) {
                // dd/mm/yyyy
                const [day, month, year] = dateString.split("/");
                date = new Date(`${year}-${month}-${day}`);
            } else {
                // yyyy-mm-dd
                date = new Date(dateString);
            }
        } else {
            // already Date object
            date = dateString;
        }

        if (isNaN(date)) return "";

        // Helper for suffix
        function getOrdinalSuffix(day) {
            if (day > 3 && day < 21) return 'th';
            switch (day % 10) {
                case 1: return 'st';
                case 2: return 'nd';
                case 3: return 'rd';
                default: return 'th';
            }
        }

        const monthNames = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];

        const dayNum = date.getDate();
        const suffix = getOrdinalSuffix(dayNum);
        const monthName = monthNames[date.getMonth()];
        const yearNum = date.getFullYear();

        return `${dayNum}${suffix} ${monthName} ${yearNum}`;
    }

    const releaseDateFormatted = formatDateLikeTimeline(certInDate);
    const periodStartFormatted = formatDateLikeTimeline(certInStartDate);
    const periodEndFormatted = formatDateLikeTimeline(certInEndDate);

    return new Table({
        width: { size: 84, type: WidthType.PERCENTAGE },
        alignment: AlignmentType.CENTER,
        spacing: { before: 500 },
        borders: {
            top: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
            bottom: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
            left: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
            right: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
        },
        rows: [
            // Report Release Date
            new TableRow({
                children: [
                    new TableCell({
                        width: { size: 33, type: WidthType.PERCENTAGE },
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: "Report Release Date",
                                        font: "Arial",
                                        size: 24,
                                        color: "000000",
                                        bold: true,
                                    }),
                                ],
                            }),
                        ],
                        margins: { top: 100, bottom: 100, left: 100, right: 100 },
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            bottom: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            left: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            right: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                        }
                    }),
                    new TableCell({
                        width: { size: 2, type: WidthType.PERCENTAGE },
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.RIGHT,
                                children: [
                                    new TextRun({
                                        text: ":",
                                        font: "Arial",
                                        size: 24,
                                        color: "000000",
                                        bold: true,
                                    }),
                                ],
                            }),
                        ],
                        margins: { top: 100, bottom: 100, left: 100, right: 100 },
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            bottom: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            left: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            right: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                        }
                    }),
                    new TableCell({
                        width: { size: 65, type: WidthType.PERCENTAGE },
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: releaseDateFormatted,
                                        font: "Arial",
                                        size: 24,
                                        color: "000000",
                                    }),
                                ],
                            }),
                        ],
                        margins: { top: 100, bottom: 100, left: 100, right: 100 },
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            bottom: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            left: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            right: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                        }
                    }),
                ],
                margins: { top: 100, bottom: 100, left: 100, right: 100 },
                borders: {
                    top: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                    bottom: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                    left: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                    right: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                }
            }),

            // Type of Audit
            new TableRow({
                children: [
                    new TableCell({
                        width: { size: 33, type: WidthType.PERCENTAGE },
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: "Type of Audit",
                                        font: "Arial",
                                        size: 24,
                                        color: "000000",
                                        bold: true,
                                    }),
                                ],
                            }),
                        ],
                        margins: { top: 100, bottom: 100, left: 100, right: 100 },
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            bottom: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            left: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            right: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                        }
                    }),
                    new TableCell({
                        width: { size: 2, type: WidthType.PERCENTAGE },
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.RIGHT,
                                children: [
                                    new TextRun({
                                        text: ":",
                                        font: "Arial",
                                        size: 24,
                                        color: "000000",
                                        bold: true,
                                    }),
                                ],
                            }),
                        ],
                        margins: { top: 100, bottom: 100, left: 100, right: 100 },
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            bottom: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            left: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            right: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                        }
                    }),
                    new TableCell({
                        width: { size: 65, type: WidthType.PERCENTAGE },
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: "Source Code Security Audit",
                                        font: "Arial",
                                        size: 24,
                                        color: "000000",
                                    }),
                                ],
                            }),
                        ],
                        margins: { top: 100, bottom: 100, left: 100, right: 100 },
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            bottom: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            left: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            right: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                        }
                    }),
                ],
                margins: { top: 100, bottom: 100, left: 100, right: 100 },
                borders: {
                    top: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                    bottom: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                    left: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                    right: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                }
            }),

            // Type of Audit Report
            new TableRow({
                children: [
                    new TableCell({
                        width: { size: 33, type: WidthType.PERCENTAGE },
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: "Type of Audit Report",
                                        font: "Arial",
                                        size: 24,
                                        color: "000000",
                                        bold: true,
                                    }),
                                ],
                            }),
                        ],
                        margins: { top: 100, bottom: 100, left: 100, right: 100 },
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            bottom: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            left: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            right: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                        }
                    }),
                    new TableCell({
                        width: { size: 2, type: WidthType.PERCENTAGE },
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.RIGHT,
                                children: [
                                    new TextRun({
                                        text: ":",
                                        font: "Arial",
                                        size: 24,
                                        color: "000000",
                                        bold: true,
                                    }),
                                ],
                            }),
                        ],
                        margins: { top: 100, bottom: 100, left: 100, right: 100 },
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            bottom: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            left: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            right: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                        }
                    }),
                    new TableCell({
                        width: { size: 65, type: WidthType.PERCENTAGE },
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: "First Audit Report",
                                        font: "Arial",
                                        size: 24,
                                        color: "000000",
                                    }),
                                ],
                            }),
                        ],
                        margins: { top: 100, bottom: 100, left: 100, right: 100 },
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            bottom: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            left: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            right: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                        }
                    }),
                ],
                margins: { top: 100, bottom: 100, left: 100, right: 100 },
                borders: {
                    top: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                    bottom: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                    left: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                    right: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                }
            }),

            // Period
            new TableRow({
                children: [
                    new TableCell({
                        width: { size: 33, type: WidthType.PERCENTAGE },
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: "Period",
                                        font: "Arial",
                                        size: 24,
                                        color: "000000",
                                        bold: true,
                                    }),
                                ],
                            }),
                        ],
                        margins: { top: 100, bottom: 100, left: 100, right: 100 },
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            bottom: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            left: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            right: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                        }
                    }),
                    new TableCell({
                        width: { size: 2, type: WidthType.PERCENTAGE },
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.RIGHT,
                                children: [
                                    new TextRun({
                                        text: ":",
                                        font: "Arial",
                                        size: 24,
                                        color: "000000",
                                        bold: true,
                                    }),
                                ],
                            }),
                        ],
                        margins: { top: 100, bottom: 100, left: 100, right: 100 },
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            bottom: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            left: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            right: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                        }
                    }),
                    new TableCell({
                        width: { size: 65, type: WidthType.PERCENTAGE },
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: `${periodStartFormatted} to ${periodEndFormatted}`,
                                        font: "Arial",
                                        size: 24,
                                        color: "000000",
                                    }),
                                ],
                            }),
                        ],
                        margins: { top: 100, bottom: 100, left: 100, right: 100 },
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            bottom: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            left: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                            right: { style: BorderStyle.SINGLE, size: 1, color: "ffffff" },
                        }
                    }),
                ],
            }),
        ],
    });
}

